import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './DocumentPreview.css';

/** View is always an in-app preview, never navigation to an object-store URL. */
export function DocumentPreview({ title, loadUrl, onClose }: {
  title: string; loadUrl: () => Promise<string>; onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const overlayId = useId();
  const closing = useRef(false);
  const [attempt, setAttempt] = useState(0);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const close = () => { if (!closing.current) { closing.current = true; window.history.back(); } };

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.showModal();
    if (window.history.state?.documentPreviewId !== overlayId) {
      window.history.pushState({ ...window.history.state, outdoorOverlay: 'document-preview', documentPreviewId: overlayId }, '');
    }
    const pop = () => onClose();
    window.addEventListener('popstate', pop);
    return () => {
      window.removeEventListener('popstate', pop);
      document.body.style.overflow = overflow;
      previousFocus?.focus({ preventScroll: true });
    };
  }, [onClose, overlayId]);

  useEffect(() => {
    let active = true;
    loadUrl().then(value => { if (active) setUrl(value); })
      .catch(() => { if (active) { setFailed(true); setLoading(false); } });
    return () => { active = false; };
  }, [loadUrl, attempt]);

  return createPortal(<dialog ref={dialog} className="op-document-preview" aria-labelledby="document-preview-title"
    onCancel={event => { event.preventDefault(); close(); }}>
    <header>
      <button type="button" aria-label="Close preview" onClick={close}>←</button>
      <h2 id="document-preview-title">{title}</h2>
    </header>
    <div className="op-document-preview-body" aria-busy={loading}>
      {loading && <p role="status">Loading image…</p>}
      {failed && <div role="alert"><p>Image could not be loaded.</p><button type="button" onClick={() => { setLoading(true); setFailed(false); setUrl(''); setAttempt(value => value + 1); }}>Try again</button></div>}
      {url && !failed && <div className="op-document-preview-image" style={{ width: `${zoom * 100}%`, height: `${zoom * 100}%` }}>
        <img src={url} alt={title} onLoad={() => setLoading(false)} onError={() => { setLoading(false); setFailed(true); }}/>
      </div>}
    </div>
    <footer aria-label="Image zoom">
      <button type="button" aria-label="Zoom out" disabled={zoom <= 1 || loading || failed} onClick={() => setZoom(value => Math.max(1, value - 0.5))}>−</button>
      <button type="button" disabled={loading || failed} onClick={() => setZoom(1)}>Reset</button>
      <button type="button" aria-label="Zoom in" disabled={zoom >= 4 || loading || failed} onClick={() => setZoom(value => Math.min(4, value + 0.5))}>＋</button>
    </footer>
  </dialog>, document.body);
}
